import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import "./DataGridTable.css";

/**
 * Composant DataGrid réutilisable avec thème standardisé
 * 
 * @param {Array} rows - Les données à afficher dans le tableau
 * @param {Array} columns - Configuration des colonnes
 * @param {boolean} loading - État de chargement
 * @param {string} storageKey - Clé pour sauvegarder le zoom dans localStorage (optionnel)
 * @param {number} zoomLevel - Niveau de zoom externe (optionnel, si non fourni, géré en interne)
 * @param {Function} onZoomChange - Callback quand le zoom change (optionnel)
 * @param {Object} initialSort - Tri initial { field: string, sort: 'asc' | 'desc' } (optionnel)
 * @param {number} pageSize - Taille de page par défaut (défaut: 25)
 * @param {Function} getRowId - Fonction pour obtenir l'ID de la ligne (défaut: (row) => row.id)
 * @param {string} containerClassName - Classe CSS personnalisée pour le conteneur (optionnel)
 * @param {string} height - Hauteur personnalisée du conteneur (optionnel)
 * @param {boolean} showZoomControls - Afficher les contrôles de zoom (défaut: true)
 * @param {Object} ...otherProps - Autres props à passer au DataGrid
 */
export default function DataGridTable({
  rows = [],
  columns = [],
  loading = false,
  storageKey,
  zoomLevel: externalZoomLevel,
  onZoomChange,
  initialSort,
  pageSize = 25,
  getRowId = (row) => row.id,
  containerClassName = "",
  height,
  showZoomControls = true,
  ...otherProps
}) {
  // Gestion du zoom (interne si non fourni en externe)
  const [internalZoomLevel, setInternalZoomLevel] = useState(() => {
    if (externalZoomLevel !== undefined) return externalZoomLevel;
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseFloat(saved) : 1.0;
    }
    return 1.0;
  });

  const zoomLevel = externalZoomLevel !== undefined ? externalZoomLevel : internalZoomLevel;

  // Sauvegarder le niveau de zoom dans localStorage si storageKey est fourni
  useEffect(() => {
    if (storageKey && externalZoomLevel === undefined) {
      localStorage.setItem(storageKey, internalZoomLevel.toString());
    }
  }, [storageKey, internalZoomLevel, externalZoomLevel]);

  // Notifier le parent si onZoomChange est fourni
  useEffect(() => {
    if (onZoomChange && externalZoomLevel === undefined) {
      onZoomChange(internalZoomLevel);
    }
  }, [internalZoomLevel, onZoomChange, externalZoomLevel]);

  // Fonctions pour ajuster le zoom
  const handleZoomIn = () => {
    if (externalZoomLevel === undefined) {
      setInternalZoomLevel(prev => {
        const newLevel = Math.min(prev + 0.1, 1.5);
        return newLevel;
      });
    } else if (onZoomChange) {
      onZoomChange(Math.min(zoomLevel + 0.1, 1.5));
    }
  };

  const handleZoomOut = () => {
    if (externalZoomLevel === undefined) {
      setInternalZoomLevel(prev => {
        const newLevel = Math.max(prev - 0.1, 0.8);
        return newLevel;
      });
    } else if (onZoomChange) {
      onZoomChange(Math.max(zoomLevel - 0.1, 0.8));
    }
  };

  const handleZoomReset = () => {
    if (externalZoomLevel === undefined) {
      setInternalZoomLevel(1.0);
    } else if (onZoomChange) {
      onZoomChange(1.0);
    }
  };

  const handleZoomSliderChange = (e) => {
    const newLevel = parseFloat(e.target.value);
    if (externalZoomLevel === undefined) {
      setInternalZoomLevel(newLevel);
    } else if (onZoomChange) {
      onZoomChange(newLevel);
    }
  };

  // Styles sx identiques à Animaux
  const gridStyles = {
    border: "none",
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    borderRadius: "12px",
    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.06)",
    "& .MuiDataGrid-columnHeaders": {
      background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
      fontWeight: 800,
      fontSize: `${1.3 * zoomLevel}rem`,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      color: "#475569",
      borderBottom: "2px solid #e2e8f0",
      minHeight: `${60 * zoomLevel}px !important`,
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 800,
        fontSize: `${1.3 * zoomLevel}rem`,
      },
    },
    "& .MuiDataGrid-row": {
      borderBottom: "1px solid #f1f5f9",
      "&:nth-of-type(even)": {
        backgroundColor: "#ffffff",
      },
      "&:nth-of-type(odd)": {
        backgroundColor: "#f8fafc",
      },
      "&:hover": {
        backgroundColor: "#f1f5f9 !important",
      },
      "&:last-child": {
        borderBottom: "none",
      },
    },
    "& .MuiDataGrid-cell": {
      fontSize: `${1.3 * zoomLevel}rem`,
      color: "#0f172a",
      padding: `${16 * zoomLevel}px ${20 * zoomLevel}px`,
      "&:focus": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "1px solid #e2e8f0",
      minHeight: "52px",
    },
    "& .MuiDataGrid-toolbarContainer": {
      padding: "12px 16px",
      borderBottom: "1px solid #e2e8f0",
    },
    "& .MuiDataGrid-toolbar": {
      "& .MuiButton-text": {
        color: "#3b82f6",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "rgba(59, 130, 246, 0.1)",
        },
      },
    },
  };

  // Configuration du tri initial
  const initialState = {
    pagination: { paginationModel: { pageSize, page: 0 } },
  };

  if (initialSort) {
    initialState.sorting = {
      sortModel: [{ field: initialSort.field, sort: initialSort.sort }],
    };
  }

  // Classe CSS du conteneur
  const containerClass = containerClassName 
    ? `data-grid-container ${containerClassName}`
    : "data-grid-container";

  // Style de hauteur personnalisé
  const containerStyle = height ? { height } : {};

  return (
    <>
      {/* Contrôles de zoom */}
      {showZoomControls && (
        <div className="zoom-controls">
          <div className="zoom-label">
            <span>🔍 Taille du texte</span>
          </div>
          <button
            onClick={handleZoomOut}
            className="zoom-button"
            aria-label="Réduire la taille"
            disabled={zoomLevel <= 0.8}
          >
            −
          </button>
          <input
            type="range"
            min="0.8"
            max="1.5"
            step="0.05"
            value={zoomLevel}
            onChange={handleZoomSliderChange}
            className="zoom-slider"
            aria-label="Niveau de zoom"
          />
          <button
            onClick={handleZoomIn}
            className="zoom-button"
            aria-label="Augmenter la taille"
            disabled={zoomLevel >= 1.5}
          >
            +
          </button>
          <span className="zoom-value">{Math.round(zoomLevel * 100)}%</span>
          <button
            onClick={handleZoomReset}
            className="zoom-reset"
            aria-label="Réinitialiser la taille"
            title="Réinitialiser à 100%"
          >
            ↺
          </button>
        </div>
      )}

      {/* DataGrid */}
      <div className={containerClass} style={containerStyle}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={initialState}
          loading={loading}
          sx={gridStyles}
          {...otherProps}
        />
      </div>
    </>
  );
}


